import { ChangeEvent, createRef, useEffect, MouseEvent, useMemo } from "react";

import { idBoundValidator, isCheckedValidator, isValidPostalCode } from "../validation.js";
import { filterString, trns } from "../helper.js";

import TextInput from "./TextInput.js";
import SelectInput from "./SelectInput.js";
import NextButton from "./NextButton.js";
import CheckInput from "./CheckInput.js";
import withStateSubmit from "./withStateSubmit.js";
import {
  StringMap,
  ThirdStateSubmit,
  ThirdState,
  Countries,
  Target,
  KeyboardEvent,
} from "../types/types.js";
import { Segment, Item, Heading, Button, Form } from "ui-components";
import useRegStore from "../store/index.js";
import { useShallow } from "zustand/react/shallow";

type Props = {
  handleField: (
    f: string,
    e:
      | Target
      | ChangeEvent<HTMLFormElement>
      | ChangeEvent<HTMLInputElement>
      | ChangeEvent<HTMLSelectElement>
  ) => void;
  changeState: (state: string, value?: string | number | boolean, e?: boolean) => void;
  state: ThirdState;
  onSubmit: (e: KeyboardEvent | MouseEvent<HTMLButtonElement>) => Promise<void>;
  reqOrOpt: (field: string) => typeof field;
  isReq: (field: string) => typeof field;
  previousPage: () => void;
  dirty: StringMap<boolean>;
  isFinal: boolean;
  isStart: boolean;
  tc_url?: string;
  showOfAge?: boolean;
  submitted?: boolean;

  // Refs
  currency_ref: typeof createRef;
  country_id_ref: typeof createRef;
  question_ref: typeof createRef;
  answer_ref: typeof createRef;
  province_id_ref: typeof createRef;
  city_ref: typeof createRef;
  address_ref: typeof createRef;
  address2_ref: typeof createRef;
  post_code_ref: typeof createRef;
  ofage_ref: typeof createRef;
  allow_email_ref: typeof createRef;
};

const state: ThirdState = {
  address: "",
  address2: null,
  city: "",
  province_id: "",
  post_code: "",
  country_id: "",
  countryId: "",
  currency: "",
  question: "",
  answer: "",
  ofage: false,
  allow_email: false,
};

const Third = ({
  handleField,
  changeState,
  state,
  onSubmit,
  reqOrOpt,
  isReq,
  previousPage,
  dirty,
  isFinal,
  isStart,
  tc_url,
  showOfAge,
  submitted,
  currency_ref,
  country_id_ref,
  question_ref,
  answer_ref,
  province_id_ref,
  city_ref,
  address_ref,
  address2_ref,
  post_code_ref,
  ofage_ref,
  allow_email_ref,
}: Props) => {
  const { countryId, provinceId, provinces, country_currencies, currencies, countries, mode } = useRegStore(
    useShallow(state => ({
      countryId: state.step3.country_id,
      provinceId: state.step3.province_id,
      provinces: state.provinces,
      country_currencies: state.country_currencies,
      currencies: state.currencies,
      countries: state.countries,
      mode: state.mode,
    }))
  );

  const getCountryCode = (country: string): string | undefined => {
    let x = countries.filter((c: Countries) => c.id === Number(country));
    return x.length > 0 ? x[0].twoCharCode : undefined;
  };

  const countryCode: string = useMemo(
    () => (countryId ? getCountryCode(countryId) || "" : ""),
    [countryId, state.country_id]
  );

  useEffect(
    () => {
      if (countryId !== state.country_id && countryId) {
        changeState("country_id", countryId);
        handleCountry({
          target: {
            value: countryId.toString(),
          },
        } as ChangeEvent<any>);
      }
    },
    [countryId, changeState]
  );

  const handleCountryCode = (v: ChangeEvent<HTMLSelectElement>) => {
    const new_country: string = v.target.value;

    if (state.country_id !== new_country) {
      const country = getCountryCode(new_country);
      if (country) {
        changeState("country_code", country);
      }
    } else if (state.country_id === "-1") {
      changeState("country_code", "");
    }

    const validPostCode = isValidPostalCode(state.country_code, state.post_code);
    const validPostal: boolean = validPostCode.status === "success" ? true : false;

    if (!validPostal) {
      changeState("post_code", "");
    }
  };

  const handleCountry = (v: ChangeEvent<HTMLSelectElement>) => {
    const new_country = v.target.value;
    const provinceOptional = () => {
      const { required } = useRegStore.getState()[mode];
      if (required.province_id) {
        useRegStore.getState().setProvinceOpt(mode);
      }
    };

    const provinceReq = () => {
      const { optional } = useRegStore.getState()[mode];
      if (optional.province_id) {
        useRegStore.getState().setProvinceReq(mode);
      }
    };

    if (!state.country_id) {
      changeState("country_code", "");
    }

    if (new_country !== countryId) {
      changeState("post_code", "");
      changeState("province_id", "");

      if (new_country) {
        const found = country_currencies[parseInt(new_country, 10)];
        if (found) {
          changeState("currency", found.name.id, true);
        } else if (state.currency) {
          changeState("currency", "");
        }
      }

      const provinceData = new_country in provinces ? provinces[new_country] : null;
      if (provinceData != null) {
        provinceReq();
      } else {
        provinceOptional();
      }
    }

    handleField("country_id", v);
  };

  const handleCheckbox = (_val: ChangeEvent<HTMLInputElement>, key: string, _cb?: Object) => {
    changeState(key, !state[key], true);
  };

  const { country_id } = state;
  const provinceData = country_id in provinces ? provinces[country_id] : null;
  const currencyData =
    country_id in country_currencies
      ? [country_currencies[country_id as number].name]
      : currencies;
  const defaulted = !(country_id in country_currencies);
  const birthdayField = reqOrOpt("year") || reqOrOpt("month") || reqOrOpt("day");

  return (
    <Form className="fieldset">
      <Heading headingLevel="h4">
        {trns("IDS_REGISTRATION_ACCOUNT_INFORMATION", "Account Information")}
      </Heading>

      <Segment className="form-groups">
        {reqOrOpt("address") && (
          <TextInput
            placeholder={trns("IDS_LOGINBOX_ADDRESS", "Address")}
            label={trns("IDS_LOGINBOX_ADDRESS_PROMPT", "Address:")}
            onInput={v => handleField("address", v)}
            value={state.address || ""}
            dirty={dirty.address}
            req={isReq("address")}
            ref={address_ref}
          />
        )}
        {reqOrOpt("address2") && (
          <TextInput
            placeholder={trns("IDS_LOGINBOX_ADDRESS_CONT", "Address Cont.")}
            label={trns("IDS_LOGINBOX_ADDRESS_CONT", "Address Cont.")}
            onInput={v => handleField("address2", v)}
            value={state.address2 || ""}
            dirty={dirty.address2}
            req={isReq("address2")}
            ref={address2_ref}
          />
        )}
        {reqOrOpt("city") && (
          <TextInput
            placeholder={trns("IDS_LOGINBOX_CITY", "City")}
            label={trns("IDS_LOGINBOX_CITY_PROMPT", "City:")}
            onInput={v => handleField("city", filterString(v))}
            value={state.city}
            dirty={dirty.city}
            type="text"
            req={isReq("city")}
            ref={city_ref}
          />
        )}
        {(reqOrOpt("country_id") || reqOrOpt("province_id")) && (
          <SelectInput
            placeholder={trns("IDS_LOGINBOX_SELECT_COUNTRY_HEADER", "Select Country")}
            label={trns("IDS_LOGINBOX_COUNTRY_PROMPT", "Country:")}
            onInput={v => {
              handleCountry(v);
              handleCountryCode(v);
            }}
            value={state.country_id}
            dirty={dirty.country_id}
            validator={v => idBoundValidator(countries, v)}
            req={isReq("country_id") || reqOrOpt("province_id")}
            ref={country_id_ref}
            options={countries}
          />
        )}

        {reqOrOpt("post_code") && (
          <TextInput
            placeholder={trns("IDS_LOGINBOX_ZIP", "Zip")}
            label={trns("IDS_LOGINBOX_ZIP_PROMPT", "Zip:")}
            onInput={v => handleField("post_code", filterString(v))}
            validator={v =>
              isValidPostalCode(countryCode ? countryCode : state.country_code, v)
            }
            value={state.post_code}
            dirty={dirty.post_code}
            req={isReq("post_code")}
            ref={post_code_ref}
            maxLength="16"
          />
        )}
        {/* For slippery: hide the province selector if we have an initial value */}
        {provinceData && (!provinceId || dirty.province_id) && (
          <SelectInput
            placeholder={trns("IDS_LOGINBOX_STATE_OR_PROVINCE", "Select state or province")}
            label={trns("IDS_LOGINBOX_STATE_PROMPT", "State:")}
            onInput={v => handleField("province_id", v)}
            value={state.province_id}
            dirty={dirty.province_id}
            validator={v => idBoundValidator(provinceData, v)}
            req={isReq("province_id")}
            ref={province_id_ref}
            options={provinceData}
          />
        )}
        {reqOrOpt("currency") && (
          <SelectInput
            placeholder={trns("IDS_LOGINBOX_CURRENCY_PROMPT", "Currency:")}
            label={trns("IDS_LOGINBOX_CURRENCY_PROMPT", "Currency:")}
            onInput={v => handleField("currency", v)}
            value={state.currency}
            dirty={dirty.currency}
            validator={v => idBoundValidator(currencyData, v)}
            req={isReq("currency")}
            ref={currency_ref}
            options={currencyData}
            defaultedOption={defaulted}
          />
        )}
        {reqOrOpt("question") && (
          <TextInput
            placeholder={trns("IDS_LOGINBOX_QUESTION", "Secret question")}
            label={trns("IDS_LOGINBOX_QUESTION_PROMPT", "Secret question:")}
            onInput={v => handleField("question", v)}
            value={state.question}
            dirty={dirty.question}
            req={isReq("question")}
            ref={question_ref}
          />
        )}
        {reqOrOpt("answer") && (
          <TextInput
            placeholder={trns("IDS_LOGINBOX_ANSWER", "Answer")}
            label={trns("IDS_LOGINBOX_ANSWER_PROMPT", "Answer:")}
            onInput={v => handleField("answer", v)}
            value={state.answer}
            dirty={dirty.answer}
            req={isReq("answer")}
            ref={answer_ref}
          />
        )}
        <Segment className="reg-checks">
          {showOfAge && reqOrOpt("ofage") && !birthdayField && (
            <CheckInput
              className="field-legal"
              text={trns("IDS_LOGINBOX_OF_LEGAL_GAMBLE_AGE", "I am of legal gambling age.")}
              value={state.ofage}
              dirty={dirty.ofage}
              onInput={(v, cb) => handleCheckbox(v, "ofage", cb)}
              validator={v => isCheckedValidator(v)}
              req={isReq("ofage")}
              ref={ofage_ref}
            />
          )}
          {reqOrOpt("allow_email") && (
            <CheckInput
              className="field-allow-email"
              text={trns("IDS_ALLOW_EMAIL", "I would like to receive promotions via email")}
              value={state.allow_email}
              dirty={dirty.allow_email}
              onInput={(v, cb) => handleCheckbox(v, "allow_email", cb)}
              req={isReq("allow_email")}
              ref={allow_email_ref}
            />
          )}
        </Segment>
        {tc_url && (
          <a rel="noopener noreferrer" target="_blank" href={tc_url}>
            {trns("IDS_TERMS_CONDITIONS", "Terms and conditions")}
          </a>
        )}
      </Segment>

      <Segment className="btn-toolbar reg-nav x">
        <Item className="grid grid-6">
          <Button
            type="button"
            className="btn"
            disabled={isStart}
            onClick={() => {
              !isStart && previousPage();
            }}
          />
        </Item>

        <Item className="grid grid-6">
          <NextButton
            disabled={submitted}
            onSubmit={onSubmit}
            isFinal={isFinal}
            isStart={isStart}
          />
        </Item>
      </Segment>
    </Form>
  );
};

export default withStateSubmit(Third, state, "step3", (t: ThirdStateSubmit) => ({
  currency_ref: (el: typeof createRef) => {
    t.currency_ref = el;
  },
  country_id_ref: (el: typeof createRef) => {
    t.country_id_ref = el;
  },
  question_ref: (el: typeof createRef) => {
    t.question_ref = el;
  },
  answer_ref: (el: typeof createRef) => {
    t.answer_ref = el;
  },
  province_id_ref: (el: typeof createRef) => {
    t.province_id_ref = el;
  },
  city_ref: (el: typeof createRef) => {
    t.city_ref = el;
  },
  address_ref: (el: typeof createRef) => {
    t.address_ref = el;
  },
  address2_ref: (el: typeof createRef) => {
    t.address2_ref = el;
  },
  post_code_ref: (el: typeof createRef) => {
    t.post_code_ref = el;
  },
  ofage_ref: (el: typeof createRef) => {
    t.ofage_ref = el;
  },
  allow_email_ref: (el: typeof createRef) => {
    t.allow_email_ref = el;
  },
}));
